import { useEffect, useState } from "react";
import { driver } from "driver.js";

export const FORCE_TOURS = import.meta.env.VITE_FORCE_TOURS === "true";
export const TOUR_ACTIVE_CHANGE = "volta-tour-active-change";
export const TOUR_RESET = "volta-tour-reset";
export const TOUR_REPLAY_HINT_SHOW = "volta-tour-replay-hint-show";
/** Delay before starting a replay so Redux "shown" clears can settle first. */
export const TOUR_REPLAY_START_DELAY_MS = 1000;

/** @typedef {"dashboard" | "general" | "enclosure" | "upgrades" | "transportation" | "roomViewKeyboard"} TourId */

/** @type {Record<TourId, { key: string, storage: "userMeta", label: string }>} */
export const TOUR_CONFIGS = {
    dashboard: { key: "dashboardTourShown", storage: "userMeta", label: "Dashboard" },
    general: { key: "generalTourShown", storage: "userMeta", label: "General" },
    enclosure: { key: "enclosureTourShown", storage: "userMeta", label: "Enclosure" },
    upgrades: { key: "upgradesTourShown", storage: "userMeta", label: "Upgrades" },
    transportation: { key: "transportationTourShown", storage: "userMeta", label: "Transportation" },
    roomViewKeyboard: {
        key: "roomViewKeyboardHintShown",
        storage: "userMeta",
        label: "Keyboard Hints",
    },
};

/** Persisted once; not listed under Account Guided Tours. */
export const TOUR_REPLAY_HINT_META_KEY = "tourReplayHintShown";

export const USER_TOUR_META_KEYS = Object.values(TOUR_CONFIGS).map((config) => config.key);

/** @type {Partial<Record<TourId, number>>} */
const tourReplayGenerations = {};

/**
 * Resolve which guided tour belongs to the current route (if any).
 * @param {string} pathname
 * @param {string} [search]
 * @returns {TourId | null}
 */
export function getTourIdForLocation(pathname = "", search = "") {
    if (pathname === "/dashboard" || pathname.startsWith("/dashboard/")) {
        return "dashboard";
    }

    const modelMatch = pathname.match(/^\/models\/[^/]+(\/.*)?$/);
    if (!modelMatch) return null;

    const tabPath = modelMatch[1] || "/";
    const normalized = tabPath === "" ? "/" : tabPath;

    if (normalized === "/" || normalized === "/general") return "general";
    if (normalized.startsWith("/enclosure")) return "enclosure";
    if (normalized.startsWith("/upgrades")) return "upgrades";
    if (normalized.startsWith("/mechanicals")) {
        const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
        if (params.get("tab") === "transportation") return "transportation";
        return null;
    }

    return null;
}

export function getTourReplayGeneration(tourId) {
    if (!tourId) return 0;
    return tourReplayGenerations[tourId] || 0;
}

/** Bumps replay for a single tour so only that page's tour restarts. */
export function resetTourSessionState(tourId) {
    if (!tourId || !TOUR_CONFIGS[tourId]) return;
    tourReplayGenerations[tourId] = (tourReplayGenerations[tourId] || 0) + 1;
    window.dispatchEvent(
        new CustomEvent(TOUR_RESET, {
            detail: { generation: tourReplayGenerations[tourId], tourId },
        })
    );
}

/** Subscribes to replay bumps for one tour; survives remounts for late-mounted tours. */
export function useTourReplayGeneration(tourId) {
    const [generation, setGeneration] = useState(() => getTourReplayGeneration(tourId));

    useEffect(() => {
        setGeneration(getTourReplayGeneration(tourId));
        const handler = (event) => {
            if (event.detail?.tourId !== tourId) return;
            setGeneration(event.detail?.generation ?? getTourReplayGeneration(tourId));
        };
        window.addEventListener(TOUR_RESET, handler);
        return () => window.removeEventListener(TOUR_RESET, handler);
    }, [tourId]);

    return generation;
}

export function showTourReplayHint() {
    window.dispatchEvent(new CustomEvent(TOUR_REPLAY_HINT_SHOW));
}

let activeTourCount = 0;

export function isTourShown(shown) {
    return FORCE_TOURS ? false : Boolean(shown);
}

export function isTourActive() {
    return activeTourCount > 0;
}

export function setTourActive(active) {
    if (active) {
        activeTourCount += 1;
    } else {
        activeTourCount = Math.max(0, activeTourCount - 1);
    }
    window.dispatchEvent(new CustomEvent(TOUR_ACTIVE_CHANGE, { detail: { active: activeTourCount > 0 } }));
}

export function useTourActive() {
    const [active, setActive] = useState(isTourActive);

    useEffect(() => {
        const handler = (event) => setActive(event.detail.active);
        window.addEventListener(TOUR_ACTIVE_CHANGE, handler);
        return () => window.removeEventListener(TOUR_ACTIVE_CHANGE, handler);
    }, []);

    return active;
}

const TOUR_ADVANCE_KEYS = new Set([" ", "Enter", "ArrowRight"]);
const TOUR_SCROLL_KEYS = new Set(["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End"]);
/** Keys driver.js owns: closing the tour and trapping focus inside the popover. */
const TOUR_PASSTHROUGH_KEYS = new Set(["Escape", "Tab"]);
const TOUR_POINTER_EVENTS = ["pointerdown", "mousedown", "pointerup", "mouseup", "dblclick", "contextmenu"];
const CAPTURE = { capture: true };
const CANCELABLE_CAPTURE = { capture: true, passive: false };
const TOUR_KEYBOARD_TIP = "<br><br><em>Use Space or the arrow keys to change steps.</em>";

function withFirstStepKeyboardTip(steps = []) {
    if (!steps.length) return steps;

    const [first, ...rest] = steps;
    const popover = first.popover || {};
    const description = popover.description || "";
    if (description.includes("Use Space or the arrow keys")) return steps;

    return [
        {
            ...first,
            popover: {
                ...popover,
                description: `${description}${TOUR_KEYBOARD_TIP}`,
            },
        },
        ...rest,
    ];
}

export function createTourDriver(overrides = {}) {
    const { stepCount, onDestroyed, onDoneClick, onHighlightStarted, ...driverOverrides } = overrides;
    let suppressReplayHint = false;
    let appLocked = false;
    let pageCleanupDone = false;

    const isPopoverTarget = (target) => Boolean(target?.closest?.(".driver-popover"));

    const addInteractionListeners = () => {
        TOUR_POINTER_EVENTS.forEach((type) => window.addEventListener(type, blockAppPointerEvent, CAPTURE));
        window.addEventListener("click", handleClick, CAPTURE);
        window.addEventListener("wheel", blockScroll, CANCELABLE_CAPTURE);
        window.addEventListener("touchmove", blockScroll, CANCELABLE_CAPTURE);
        window.addEventListener("keydown", handleKeyDown, CAPTURE);
        window.addEventListener("keyup", handleKeyUp, CAPTURE);
    };

    const removeInteractionListeners = () => {
        TOUR_POINTER_EVENTS.forEach((type) => window.removeEventListener(type, blockAppPointerEvent, CAPTURE));
        window.removeEventListener("click", handleClick, CAPTURE);
        window.removeEventListener("wheel", blockScroll, CAPTURE);
        window.removeEventListener("touchmove", blockScroll, CAPTURE);
        window.removeEventListener("keydown", handleKeyDown, CAPTURE);
        window.removeEventListener("keyup", handleKeyUp, CAPTURE);
    };

    // driver.js skips onDestroyed when destroy() runs with no active step — always unlock here.
    const cleanupPageLocks = () => {
        if (pageCleanupDone) return;
        pageCleanupDone = true;
        document.body.style.overflow = "";
        document.querySelectorAll(".driver-overlay").forEach((el) => el.remove());
        document.body.classList.remove("driver-active", "driver-fade", "driver-simple");
        if (appLocked) {
            setTourActive(false);
            removeInteractionListeners();
            appLocked = false;
        }
    };

    // Keep presses/releases from reaching the app; the click handler decides what they mean.
    const blockAppPointerEvent = (event) => {
        if (!tourDriver.isActive() || isPopoverTarget(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
    };

    // Anything outside the popover cancels the tour rather than acting on the page behind it.
    const handleClick = (event) => {
        if (!tourDriver.isActive() || isPopoverTarget(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        tourDriver.destroy();
    };

    // Steps position themselves against the viewport, so only programmatic scrolling is allowed.
    const blockScroll = (event) => {
        if (!tourDriver.isActive() || isPopoverTarget(event.target)) return;
        if (event.cancelable) event.preventDefault();
        event.stopPropagation();
    };

    const handleKeyDown = (event) => {
        if (!tourDriver.isActive() || TOUR_PASSTHROUGH_KEYS.has(event.key)) return;

        if (event.key === "ArrowLeft") {
            // Handle on keydown so we can no-op on the first step (driver.js would close the tour).
            event.preventDefault();
            event.stopPropagation();
            if (tourDriver.hasPreviousStep()) {
                tourDriver.movePrevious();
            }
            return;
        }

        if (!TOUR_ADVANCE_KEYS.has(event.key)) {
            // Stop app shortcuts and typing into whatever was focused before the tour started.
            if (TOUR_SCROLL_KEYS.has(event.key)) event.preventDefault();
            event.stopPropagation();
            return;
        }

        event.stopPropagation();
        // Driver.js already advances on ArrowRight keyup; avoid double-advancing.
        if (event.key === "ArrowRight") return;
        // Let the focused Next/Done button handle Space/Enter natively.
        if (event.target?.closest?.(".driver-popover-next-btn")) return;

        event.preventDefault();
        document.querySelector(".driver-popover-next-btn")?.click();
    };

    // Block driver.js ArrowLeft keyup after we handle it on keydown (prevents double-step / close).
    const handleKeyUp = (event) => {
        if (!tourDriver.isActive() || event.key !== "ArrowLeft") return;
        event.stopImmediatePropagation();
    };

    const tourDriver = driver({
        showProgress: stepCount === undefined ? true : stepCount > 1,
        progressText: "{{current}} of {{total}}",
        allowClose: true,
        allowKeyboardControl: true,
        disableActiveInteraction: true,
        showButtons: ["next", "close"],
        ...driverOverrides,
        onHighlightStarted: (element, step, opts) => {
            document.body.style.overflow = "hidden";
            pageCleanupDone = false;
            onHighlightStarted?.(element, step, opts);
        },
        onDoneClick: (element, step, opts) => {
            if (onDoneClick) {
                onDoneClick(element, step, opts);
            } else {
                opts.driver.destroy();
            }
        },
        onDestroyed: (element, step, opts) => {
            cleanupPageLocks();
            if (!suppressReplayHint) {
                showTourReplayHint();
            }
            onDestroyed?.(element, step, opts);
        },
    });

    const originalSetSteps = tourDriver.setSteps.bind(tourDriver);
    tourDriver.setSteps = (steps) => originalSetSteps(withFirstStepKeyboardTip(steps));

    const originalDrive = tourDriver.drive.bind(tourDriver);
    tourDriver.drive = (stepIndex) => {
        pageCleanupDone = false;
        if (!appLocked) {
            setTourActive(true);
            addInteractionListeners();
            appLocked = true;
        }
        return originalDrive(stepIndex);
    };

    const originalDestroy = tourDriver.destroy.bind(tourDriver);
    tourDriver.destroy = () => {
        originalDestroy();
        cleanupPageLocks();
    };

    tourDriver.suppressReplayHint = () => {
        suppressReplayHint = true;
    };

    return tourDriver;
}
